/** When the daily prep digest arrives: the evening before, or the morning itself. */
export const PREP_DIGEST_MOMENTS = ['evening', 'morning'] as const;
export type PrepDigestMoment = (typeof PREP_DIGEST_MOMENTS)[number];

/**
 * The times a digest can be set to, in minutes after midnight in the user's
 * own timezone. A short list rather than a free field: an evening digest at
 * 03:00 is a morning digest nobody meant to choose.
 */
export const PREP_DIGEST_TIME_OPTIONS: Record<PrepDigestMoment, readonly number[]> = {
  evening: [15 * 60, 16 * 60, 17 * 60, 18 * 60, 19 * 60, 20 * 60],
  morning: [6 * 60, 6 * 60 + 30, 7 * 60, 7 * 60 + 30, 8 * 60, 8 * 60 + 30, 9 * 60],
};

export const DEFAULT_PREP_DIGEST_MINUTES: Record<PrepDigestMoment, number> = {
  evening: 16 * 60,
  morning: 7 * 60 + 30,
};

export const DEFAULT_PREP_DIGEST_MOMENT: PrepDigestMoment = 'evening';

export function isPrepDigestTime(moment: PrepDigestMoment, minutes: number): boolean {
  return PREP_DIGEST_TIME_OPTIONS[moment].includes(minutes);
}

/**
 * The stored time as one the settings page can show. A row written under an
 * older list of options falls back to the moment's default rather than to a
 * value nobody can pick.
 */
export function toPrepDigestMinutes(
  moment: PrepDigestMoment,
  minutes: number | null | undefined,
): number {
  return typeof minutes === 'number' && isPrepDigestTime(moment, minutes)
    ? minutes
    : DEFAULT_PREP_DIGEST_MINUTES[moment];
}

/** "16:00" for 960. */
export function formatPrepDigestTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

/** What the user chose about the notifications Wundo sends them. */
export interface NotificationPreferencesResponse {
  /** Whether the daily digest of the coming conversations is sent at all. */
  prep_digest: boolean;
  prep_digest_moment: PrepDigestMoment;
  /** Minutes after midnight, one of `PREP_DIGEST_TIME_OPTIONS[prep_digest_moment]`. */
  prep_digest_minutes: number;
  /** The notifications after a meeting: the verslag is ready, or nobody wrote one up. */
  summary_notifications: boolean;
}

export type NotificationPreferencesInput = NotificationPreferencesResponse;
